import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../app/store";
import {
  setLegalStatus,
  setLegalIndividualName,
  setPublicCompanyName,
  setCompanyDescription,
  setWebsite,
  setCompanyLogo,
  setOnlineContactInfo,
} from "../../reducer/registrationReducer";
import { setCompanyInformationModal } from "../../reducer/modalReducer";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useOwnProfile } from "../../hooks/useProfile";
import { setFieldCompleted } from "../../reducer/companyInformationReducer";

function CompanyInformationModal() {
  const dispatch = useDispatch();
  const { getOwnProfile } = useOwnProfile();

  const {
    legalStatus,
    legalIndividualName,
    publicCompanyName,
    companyDescription,
    website,
    companyLogo,
    onlineContactInfo,
  } = useSelector((state: RootState) => state.registration);
  const { companyInformationModal } = useSelector(
    (state: RootState) => state.modal
  );

  const [loading, setLoading] = useState<boolean>(false);

  const profile = useSelector((state: RootState) => state.profile.profile);

  const [editedLegalStatus, setEditedLegalStatus] = useState(legalStatus);
  const [editedLegalIndividualName, setEditedLegalIndividualName] =
    useState(legalIndividualName);
  const [editedPublicCompanyName, setEditedPublicCompanyName] =
    useState(publicCompanyName);
  const [editedCompanyDescription, setEditedCompanyDescription] =
    useState(companyDescription);
  const [editedWebsite, setEditedWebsite] = useState(website);
  const [editedCompanyLogo, setEditedCompanyLogo] = useState<string | null>(
    companyLogo
  );
  const [editedOnlineContactInfo, setEditedOnlineContactInfo] =
    useState<{ type: string; value: string }[]>(onlineContactInfo);

  const [errors, setErrors] = useState({
    legalStatus: "",
    legalIndividualName: "",
    publicCompanyName: "",
    companyDescription: "",
    website: "",
    // companyLogo: "",
    onlineContactInfo: "",
  });

  const validateFields = () => {
    const websiteRegex = /^(https?:\/\/)?[\w.-]+\.[a-zA-Z]{2,}$/;
    const imageFileTypes = ["image/jpeg", "image/png"];
    const maxFileSize = 4 * 1024 * 1024; // 4MB

    const newErrors = {
      legalStatus: !editedLegalStatus ? "Legal status is required" : "",
      legalIndividualName: !editedLegalIndividualName
        ? "Legal individual name is required"
        : "",
      publicCompanyName: !editedPublicCompanyName
        ? "Public company name is required"
        : "",
      companyDescription: !editedCompanyDescription
        ? "Company description is required"
        : "",
      website: !editedWebsite
        ? "Website is required"
        : !websiteRegex.test(editedWebsite)
        ? "Enter a valid website URL"
        : "",
      // companyLogo: editedCompanyLogo
      //   ? !imageFileTypes.includes(editedCompanyLogo.type)
      //     ? "Logo must be a JPG or PNG image"
      //     : editedCompanyLogo.size > maxFileSize
      //     ? "Logo must be smaller than 4MB"
      //     : ""
      //   : "Company logo is required",
      onlineContactInfo: editedOnlineContactInfo.some((info) => {
        if (!info.type || !info.value) return true;
        if (info.type === "website" && !websiteRegex.test(info.value)) {
          return true;
        }
        return false;
      })
        ? "All contact info fields must be completed and valid"
        : "",
    };

    setErrors(newErrors);

    // Dispatch field validation status to Redux

    return Object.values(newErrors).every((error) => !error);
  };

  useEffect(() => {
    if (profile) {
      setEditedLegalStatus(profile.type || legalStatus);
      setEditedLegalIndividualName(
        profile.legalCompanyName ||
          profile.legalIndividualName ||
          legalIndividualName
      );
      setEditedPublicCompanyName(
        profile.publicCompanyName || publicCompanyName
      );
      setEditedWebsite(profile.website || website);
      setEditedCompanyDescription(
        profile.companyDescription || companyDescription
      );
      setEditedCompanyLogo(
        typeof profile.logo === "string" ? profile.logo : null
      ); // Ensure string or null
    }
    console.log("profile data", profile);
  }, [profile, legalStatus, publicCompanyName, website, companyLogo]);

  const handleSaveChanges = () => {
    if (validateFields()) {
      dispatch(setLegalStatus(editedLegalStatus));
      dispatch(setLegalIndividualName(editedLegalIndividualName));
      dispatch(setPublicCompanyName(editedPublicCompanyName));
      dispatch(setCompanyDescription(editedCompanyDescription));
      dispatch(setWebsite(editedWebsite));
      dispatch(setCompanyLogo(editedCompanyLogo));
      dispatch(setOnlineContactInfo(editedOnlineContactInfo));
      dispatch(setCompanyInformationModal(false));
      dispatch(setFieldCompleted(true));
    }
  };

  const handleCancelEdit = () => {
    dispatch(setCompanyInformationModal(false));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setEditedCompanyLogo(reader.result as string); // Set Base64 string
      };
      reader.readAsDataURL(file);
    } else {
      setEditedCompanyLogo(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await getOwnProfile({ setLoading });
    };

    fetchData();
  }, []);

  const handleBackdropClick = (event: any) => {
    if (event.target === event.currentTarget) {
      handleCancelEdit();
    }
  };

  return (
    <div
      className={`fixed z-50 top-0 left-0 w-full h-full flex items-center justify-center bg-gray-500 overflow-scroll bg-opacity-75 ${
        companyInformationModal ? "flex" : "hidden"
      }`}
      onClick={handleBackdropClick}
    >
      <div className={Classname.modalContent}>
        <div className={Classname.title}>Edit Company Information</div>
        <div>How do you run your business?</div>
        <div className={Classname.optionContainer}>
          <div
            onClick={() => setEditedLegalStatus("company")}
            className={`${Classname.option} ${
              editedLegalStatus === "company"
                ? "border-primary"
                : "border-gray-300"
            }`}
          >
            <img src="/Company.svg" alt="" className="w-5 h-5" /> As a
            registered company
          </div>
          <div
            onClick={() => setEditedLegalStatus("individual")}
            className={`${Classname.option} ${
              editedLegalStatus === "individual"
                ? "border-primary"
                : "border-gray-300"
            }`}
          >
            <img src="/profile.svg" alt="" className="w-5 h-5" /> As a
            registered individual
          </div>
        </div>
        {errors.legalStatus && (
          <div className="text-red-500 text-sm">{errors.legalStatus}</div>
        )}
        <div className="font-semibold mt-5">
          Legal {editedLegalStatus === "individual" ? "individual" : "company"}{" "}
          name
        </div>
        <input
          type="text"
          value={editedLegalIndividualName}
          onChange={(e) => setEditedLegalIndividualName(e.target.value)}
          className={Classname.input}
        />
        {errors.legalIndividualName && (
          <div className="text-red-500 text-sm">
            {errors.legalIndividualName}
          </div>
        )}
        <div className="font-semibold mt-5">Company logo</div>
        <div className={Classname.logoContainer}>
          <div className={Classname.logoBox}>
            {editedCompanyLogo ? (
              <img
                src={editedCompanyLogo} // Directly use Base64 string
                alt="Selected Logo"
                className="object-contain w-full h-full"
              />
            ) : (
              <img src="/imageplaceholder.png" alt="Placeholder" />
            )}
          </div>
          <label className={Classname.uploadLabel} htmlFor="logoUpload">
            Upload Logo
          </label>
          <input
            type="file"
            id="logoUpload"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          <img src="/iconUpload.svg" alt="" className="h-5" />
        </div>
        {/* {errors.companyLogo && (
          <div className="text-red-500 text-sm">{errors.companyLogo}</div>
        )} */}
        <div className="text-sm text-gray-500 mt-2">
          *.png, *.jpg files up to 4MB at least 180px x 180px
        </div>
        <div className="font-semibold mt-5">Public company name</div>
        <input
          type="text"
          value={editedPublicCompanyName}
          onChange={(e) => setEditedPublicCompanyName(e.target.value)}
          className={Classname.input}
        />
        {errors.publicCompanyName && (
          <div className="text-red-500 text-sm">{errors.publicCompanyName}</div>
        )}
        <div className="font-semibold mt-5">Company description</div>
        <textarea
          cols={10}
          rows={30}
          value={editedCompanyDescription}
          onChange={(e) => setEditedCompanyDescription(e.target.value)}
          className={`${Classname.input} h-20`}
        />
        {errors.companyDescription && (
          <div className="text-red-500 text-sm">
            {errors.companyDescription}
          </div>
        )}
        {editedOnlineContactInfo?.length === 0 && (
          <div className="flex flex-col">
            <div className="font-semibold mt-5">Website</div>
            <div className="flex flex-1">
              <input
                type="text"
                value={editedWebsite}
                onChange={(e) => {
                  let data = [...editedOnlineContactInfo];
                  data.push({
                    type: "",
                    value: "",
                  });
                  data[0].value = e.target.value;

                  setEditedOnlineContactInfo(data);
                }}
                className={Classname.input}
              />
            </div>
          </div>
        )}
        {editedOnlineContactInfo?.map((info, index) => (
          <div className="flex flex-col mb-3" key={index}>
            <div className="font-semibold mt-5">Website</div>
            <div className="flex flex-1 ml-4">
              <input
                type="text"
                value={info.value}
                onChange={(e) => {
                  let data = [...editedOnlineContactInfo];
                  data[index].value = e.target.value;
                  setEditedWebsite(e.target.value);
                  setEditedOnlineContactInfo(data);
                }}
                className={Classname.input}
              />
            </div>
          </div>
        ))}
        {errors.website && (
          <div className="text-red-500 text-sm mt-2">{errors.website}</div>
        )}
        {/* 
        <button
          className={Classname.addButton}
          onClick={() => {
            let data = [...editedOnlineContactInfo, { type: "", value: "" }];
            setEditedOnlineContactInfo(data);
          }}
        >
          + Add another website
        </button> */}

        <div className={Classname.buttonContainer}>
          <button onClick={handleCancelEdit} className={Classname.cancelButton}>
            Cancel
          </button>
          <button onClick={handleSaveChanges} className={Classname.saveButton}>
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default CompanyInformationModal;

const Classname = {
  modalContent:
    "bg-white p-6 w-fit rounded shadow-md max-h-[90vh] overflow-scroll",
  title: "font-bold mb-2",
  optionContainer:
    "flex justify-around mt-2 flex-col lg:flex-row items-center gap-2",
  option:
    "w-72 border cursor-pointer rounded-md p-2 pl-0 flex gap-4 justify-center items-center",
  input: "w-full border border-gray-300 p-2 mt-2 rounded-md h-10",
  logoContainer:
    "flex items-center mt-2 border rounded-lg border-gray-200 p-3 w-fit gap-2",
  logoBox: "w-14 h-10 bg-gray-200 flex items-center justify-center rounded-md",
  uploadLabel: "text-blue-500 ml-2 cursor-pointer",
  select:
    "w-full border border-gray-300 p-2 mt-2 rounded-md h-10 cursor-pointer",
  addButton: "bg-white text-blue-500 px-4 py-2 rounded mt-4",
  buttonContainer: "mt-4 flex justify-end",
  cancelButton: "px-4 py-2 rounded-lg border-blue-500 border",
  saveButton: "ml-2 bg-blue-500 text-white px-4 py-2 rounded-lg",
};
